import React, { useState, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';

interface ScrollDownFABProps {
  className?: string;
  scrollTarget?: string; // CSS selector for the target element to scroll to
  tooltipText?: string; // Custom tooltip text
}

const ScrollDownFAB: React.FC<ScrollDownFABProps> = ({ 
  className = '', 
  scrollTarget = '.scroll-target',
  tooltipText = 'Scroll for more details'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isTooltipDismissed, setIsTooltipDismissed] = useState(false);
  const isMobile = useIsMobile();

  // No localStorage loading - dismissal is session-only

  useEffect(() => {
    const handleScroll = () => {
      // Hide FAB after scrolling 200px from top
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsVisible(scrollTop < 200);
    };

    // Listen for scroll events
    window.addEventListener('scroll', handleScroll);
    
    // Check initial position
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleClick = () => {
    // Use our custom mobile breakpoint (1024px)
    
    // Use different targets for mobile vs desktop
    let targetSelector = scrollTarget;
    if (isMobile && scrollTarget === '.scroll-target') {
      // On mobile, if the default scroll target is being used, try image gallery first
      targetSelector = '#image-gallery';
    }
    
    const targetElement = document.querySelector(targetSelector);
    if (targetElement) {
      targetElement.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    } else if (isMobile && scrollTarget === '.scroll-target') {
      // If image gallery doesn't exist on mobile, try the original target
      const fallbackElement = document.querySelector(scrollTarget);
      if (fallbackElement) {
        fallbackElement.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      } else {
        // Final fallback: scroll down by viewport height
        window.scrollBy({ 
          top: window.innerHeight * 0.8, 
          behavior: 'smooth' 
        });
      }
    } else {
      // Fallback: scroll down by viewport height
      window.scrollBy({ 
        top: window.innerHeight * 0.8, 
        behavior: 'smooth' 
      });
    }
  };

  const handleTooltipDismiss = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the FAB click
    setIsTooltipDismissed(true);
    // No localStorage - dismissal is session-only
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.8 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className={`fixed z-[110] w-full flex justify-center pointer-events-none ${isMobile ? 'bottom-40' : 'bottom-8'} ${className}`}
        >
          <div className="relative" style={{ isolation: 'isolate' }}>
            <motion.button
              onClick={handleClick}
              className="group relative bg-gradient-to-br from-jamaica-green to-jamaica-green/90 hover:from-jamaica-green/90 hover:to-jamaica-green text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 focus:outline-none focus:ring-4 focus:ring-jamaica-green/20 border-2 border-white pointer-events-auto touch-manipulation min-h-[56px] min-w-[56px] cursor-pointer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              animate={{ 
                y: [0, -8, 0],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                y: { duration: 2, repeat: Infinity, ease: "easeInOut" },
                rotate: { duration: 4, repeat: Infinity, ease: "easeInOut" }
              }}
              style={{ 
                touchAction: 'manipulation',
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                isolation: 'isolate',
                zIndex: 111
              }}
            >
              {/* Background glow effect */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-jamaica-green/40 to-jamaica-green/20 blur-lg transform scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              
              {/* Main icon */}
              <div className="relative pointer-events-none">
                <ChevronDown className="h-6 w-6 pointer-events-none" />
              </div>
              
              {/* Animated border accent */}
              <div className="absolute inset-0 rounded-full border-2 border-jamaica-yellow/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-pulse pointer-events-none"></div>
              
              {/* Tooltip - Always visible on mobile (unless dismissed), hover on desktop */}
              <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 transition-opacity duration-300 whitespace-nowrap pointer-events-none ${
                isTooltipDismissed 
                  ? `opacity-0 ${!isMobile ? 'group-hover:opacity-100' : ''}` 
                  : `${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`
              }`}>
                <div className="relative px-3 py-2 bg-gray-900 text-white text-sm rounded-lg">
                  {tooltipText}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
                  
                  {/* Dismissible X badge - positioned exactly like cart count badge but as div to avoid nested button error */}
                  {!isTooltipDismissed && (
                    <div
                      onClick={handleTooltipDismiss}
                      className={`absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 pointer-events-auto shadow-md z-10 cursor-pointer ${isMobile ? 'block' : 'hidden'}`}
                      aria-label="Dismiss tooltip"
                      style={{ touchAction: 'manipulation' }}
                    >
                      <X className="w-3 h-3 pointer-events-none" />
                    </div>
                  )}
                </div>
              </div>
            </motion.button>
            
            {/* Secondary accent elements - Now positioned relative to button container */}
            <motion.div
              className="absolute -top-2 -right-2 w-3 h-3 bg-jamaica-yellow rounded-full border border-white pointer-events-none"
              animate={{ 
                scale: [1, 1.3, 1],
                opacity: [0.7, 1, 0.7]
              }}
              transition={{ 
                duration: 1.5, 
                repeat: Infinity, 
                ease: "easeInOut",
                delay: 0.5
              }}
            />
            <motion.div
              className="absolute -bottom-1 -left-1 w-2 h-2 bg-jamaica-green/60 rounded-full border border-white/60 pointer-events-none"
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity, 
                ease: "easeInOut",
                delay: 1
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScrollDownFAB; 